import React from 'react';
import classNames from 'classnames';
import s from './ImageLinkContainer.scss';
import {DataHook} from '../ProductItem';
import {withGlobals} from '../../../../globalPropsContext';
import {IGalleryGlobalProps} from '../../../../gallery/galleryGlobalStrategy';
import {HoverType, IProduct, RibbonPlacementId} from '../../../../types/galleryTypes';
import {ProductMedia} from '../ProductMedia/ProductMedia';
import {ConditionalRender} from '../../../../category/components/ConditionalRender/ConditionalRender';
import {withTranslations} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';

export type ImageLinkContainerProps = IGalleryGlobalProps & {
  product: IProduct;
  isLiveSiteMode: boolean;
  isPreviewMode: boolean;
  productPageUrl: string;
  showHoverPreview: boolean;
  canAddProductToCart: boolean;
  handleProductItemClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  renderRibbon: (ribbonPlacementId: RibbonPlacementId) => React.JSX.Element;
  renderQuickViewButton: () => React.JSX.Element;
};

export const ImageLinkContainer: React.FunctionComponent<ImageLinkContainerProps> = withGlobals(
  withTranslations()((props) => {
    const {
      product,
      isLiveSiteMode,
      isPreviewMode,
      productPageUrl,
      showHoverPreview,
      canAddProductToCart,
      handleProductItemClick,
      renderRibbon,
      renderQuickViewButton,
      globals: {
        productsVariantInfoMap,
        shouldShowMobile,
        imageMode,
        imageRatio,
        textsMap: {digitalProductBadgeAriaLabelText},
        styles,
        stylesParams,
      },
    } = props;

    const productVariantInfo = productsVariantInfoMap?.[product.id];

    const currentMedia = productVariantInfo?.mediaItems ?? product.media;

    const hoverType = styles.get(stylesParams.gallery_hoverType).value;

    const renderImage = () => {
      const productWithCurrentMedia = canAddProductToCart
        ? {
            ...product,
            media: currentMedia,
          }
        : product;

      return (
        <ProductMedia
          classNames={{thumbnail: s.productThumbnail, image: s.productImage}}
          product={productWithCurrentMedia}
          isMobile={shouldShowMobile}
          hoverType={hoverType}
          imageRatioId={imageRatio}
          imageModeId={imageMode}
          globals={props.globals}
          textsMap={{digitalProductBadgeAriaLabel: digitalProductBadgeAriaLabelText}}>
          {product.ribbon && (
            <ConditionalRender by={'shouldShowRibbonOnImage'} className={s.ribbonContainer}>
              {renderRibbon(RibbonPlacementId.OnImage)}
            </ConditionalRender>
          )}
          {!shouldShowMobile && (
            <ConditionalRender by={'showQuickView'} className={s.quickViewButtonContainer}>
              {renderQuickViewButton()}
            </ConditionalRender>
          )}
        </ProductMedia>
      );
    };

    const classes = classNames(
      s.productItemLink,
      s.customizePadding,
      shouldShowMobile || (hoverType === HoverType.Alternate && currentMedia.length < 2) ? s.none : s[hoverType],
      {
        [s.hoverPreview]: showHoverPreview,
      }
    );

    return (
      <a
        href={isLiveSiteMode || isPreviewMode ? productPageUrl : null}
        tabIndex={-1}
        onClick={handleProductItemClick}
        className={classes}
        data-hook={DataHook.LinkContainer}>
        {renderImage()}
      </a>
    );
  })
);
